import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { DialogShell } from './DialogShell';

interface DangerConfirmDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly title: string;
  readonly message: string;
  readonly warningItems: string[];
  readonly checkboxLabel: string;
  readonly confirmText: string;
  readonly cancelText: string;
  /** 'nested' para apilar sobre otra ventana ya abierta. */
  readonly level?: 'base' | 'nested';
}

export const DangerConfirmDialog: React.FC<DangerConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  warningItems,
  checkboxLabel,
  confirmText,
  cancelText,
  level = 'base'
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleClose = () => {
    setIsChecked(false);
    onClose();
  };

  const handleConfirm = () => {
    if (!isChecked) return;
    onConfirm();
    handleClose();
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      showCloseButton={false}
      level={level}
      header={(
        <div className="sticky top-0 z-10 bg-red-600 text-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500 rounded-lg">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold">{title}</h3>
          </div>
        </div>
      )}
    >
      {/* Mensaje principal */}
      <p className="text-slate-700 mb-4">{message}</p>

      {/* Lista de advertencias */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <p className="text-red-800 font-semibold mb-2 text-sm uppercase tracking-wide">
          ⚠️ Consecuencias de esta acción:
        </p>
        <ul className="space-y-2">
          {warningItems.map((item) => (
            <li key={item} className="flex items-start gap-2 text-red-700 text-sm">
              <span className="text-red-500 mt-0.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Checkbox de confirmación */}
      <label className="flex items-start gap-3 p-3 bg-slate-100 rounded-lg cursor-pointer hover:bg-slate-200 transition-colors">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
          className="mt-1 w-5 h-5 text-red-600 border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
        />
        <span className="text-sm text-slate-700 font-medium">{checkboxLabel}</span>
      </label>

      {/* Botones */}
      <div className="flex gap-3 justify-end mt-6">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors active:scale-[0.98]"
        >
          {cancelText}
        </button>
        <button
          onClick={handleConfirm}
          disabled={!isChecked}
          className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors active:scale-[0.98] ${
            isChecked
              ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
              : 'bg-red-300 cursor-not-allowed'
          }`}
        >
          {confirmText}
        </button>
      </div>
    </DialogShell>
  );
};
